/**
 * Modelo: RespuestaLlamada
 * 
 * Representa un tipo de respuesta posible para una gestión de llamada.
 * Se usa para poblar el dropdown de "Respuesta" en el diálogo de Ingresar Gestión.
 * 
 * Tabla: TPSC_RESPUESTA_LLAMADAS
 * 
 * Legacy Reference:
 * - Tpsc_respuesta_llamadasdto.java
 * - CallManagementInterfaceImpl.java - carga dropdown de respuestas
 * 
 * @author Sura - Migración GWT a Angular
 */
export interface RespuestaLlamada {
  /** Consecutivo de la respuesta (PK) */
  nmconsecutivo: number;
  
  /** Descripción de la respuesta */
  dsrespuesta: string;
  
  /** Indica si aplica programar nueva llamada (S/N) */
  snAplicaNuevaLlamada: string;
  
  /** Horas para calcular fecha/hora de nueva llamada */
  nmhorasNuevaLlamada: number | null;
  
  /** Indica si se muestra en programación (S/N) */
  snEnProgramacion?: string;
  
  /** Indica si está activo (S/N) */
  snActivo?: string;
}

/**
 * Constantes para códigos especiales de respuestas
 * 
 * Lógica de campos condicionales:
 * - Si snAplicaNuevaLlamada = 'S', mostrar campos de fecha/hora nueva llamada
 * - Si nmconsecutivo = CLIENTE_REPORTADO (21), mostrar campo Motivo del Reporte
 * - Si nmconsecutivo IN (19,20,21), mostrar campo Evidencia
 */
export const RESPUESTA_LLAMADA_CODES = {
  /** Código para "NO CONTESTA" - 19 */
  NO_CONTESTA: 19,
  
  /** Código para "NO CONTESTA / NO EXISTE" - 20 */
  NO_CONTESTA_NO_EXISTE: 20,
  
  /** Código para "CLIENTE REPORTADO" - 21 - muestra Motivo del Reporte */
  CLIENTE_REPORTADO: 21,
  
  /** Códigos que requieren evidencia */
  REQUIERE_EVIDENCIA: [19, 20, 21]
} as const;

/**
 * Verifica si una respuesta requiere campos de nueva llamada
 */
export function requiereNuevaLlamada(respuesta: RespuestaLlamada | null): boolean {
  return respuesta?.snAplicaNuevaLlamada === 'S';
}

/**
 * Verifica si una respuesta requiere campo de Motivo del Reporte
 */
export function requiereMotivoReporte(respuesta: RespuestaLlamada | null): boolean {
  return respuesta?.nmconsecutivo === RESPUESTA_LLAMADA_CODES.CLIENTE_REPORTADO;
}

/**
 * Verifica si una respuesta requiere campo de Evidencia
 */
export function requiereEvidencia(respuesta: RespuestaLlamada | null): boolean {
  const codigo = respuesta?.nmconsecutivo;
  return codigo !== undefined && (RESPUESTA_LLAMADA_CODES.REQUIERE_EVIDENCIA as readonly number[]).includes(codigo);
}
